import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, UserPlus, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AthleteCreate = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    sport: 'Basketball',
    position: '',
    team: '',
    number: '',
    date_of_birth: '',
    height: '',
    grade: '',
    school: '',
    graduation_year: '',
    bio: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/athletes/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          number: parseInt(form.number) || 0,
          graduation_year: form.graduation_year || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Profile created!');
        navigate(`/athlete/${data.id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create profile');
      }
    } catch {
      toast.error('Error creating profile');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-rooam-black">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/athletes')} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 text-sm" data-testid="back-to-athletes">
          <ArrowLeft className="h-4 w-4" />
          Back to Athletes
        </button>

        <Card className="bg-rooam-gray-medium border-rooam-gray-light">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-rooam-blue" />
              Create Your Athlete Profile
            </CardTitle>
            <p className="text-sm text-gray-400">Fill in your info to get listed. You can connect to team rosters and games after creation.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-rooam-black border-rooam-gray-light text-white" required data-testid="athlete-name-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">Sport *</Label>
                  <Select value={form.sport} onValueChange={(v) => setForm({...form, sport: v})}>
                    <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white" data-testid="athlete-sport-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                      <SelectItem value="Basketball">Basketball</SelectItem>
                      <SelectItem value="Soccer">Soccer</SelectItem>
                      <SelectItem value="Volleyball">Volleyball</SelectItem>
                      <SelectItem value="Football">Football</SelectItem>
                      <SelectItem value="Flag Football">Flag Football</SelectItem>
                      <SelectItem value="Lacrosse">Lacrosse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Position</Label>
                  <Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} placeholder="PG, SF, GK..." className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">Team / Organization</Label>
                  <Input value={form.team} onChange={(e) => setForm({...form, team: e.target.value})} className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Jersey #</Label>
                  <Input type="number" value={form.number} onChange={(e) => setForm({...form, number: e.target.value})} className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-gray-300">Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} className="bg-rooam-black border-rooam-gray-light text-white" data-testid="athlete-dob-input" />
                </div>
                <div>
                  <Label className="text-gray-300">Height</Label>
                  <Input value={form.height} onChange={(e) => setForm({...form, height: e.target.value})} placeholder="6'2&quot;" className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Grade</Label>
                  <Input value={form.grade} onChange={(e) => setForm({...form, grade: e.target.value})} placeholder="Jr, Sr..." className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">School</Label>
                  <Input value={form.school} onChange={(e) => setForm({...form, school: e.target.value})} className="bg-rooam-black border-rooam-gray-light text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Graduation Year</Label>
                  <Select value={form.graduation_year} onValueChange={(v) => setForm({...form, graduation_year: v})}>
                    <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white" data-testid="athlete-gradyear-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                      {Array.from({ length: 8 }, (_, i) => String(new Date().getFullYear() + i)).map(yr => (
                        <SelectItem key={yr} value={yr}>{yr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Bio</Label>
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm({...form, bio: e.target.value})}
                  rows={3}
                  placeholder="Tell us about yourself..."
                  className="w-full rounded-md bg-rooam-black border border-rooam-gray-light text-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-rooam-blue"
                />
              </div>

              <Button type="submit" disabled={saving} className="w-full h-12 bg-rooam-blue hover:bg-rooam-blue-light text-black font-bold text-base" data-testid="create-profile-submit">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><UserPlus className="h-5 w-5 mr-2" />Create Profile</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AthleteCreate;
